import { useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, UtensilsCrossed, Settings, Images, Tag, Star, Inbox, ExternalLink } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { AdminLogin } from "../components/admin/AdminLogin";
import { MenuManager } from "../components/admin/MenuManager";
import { SettingsManager } from "../components/admin/SettingsManager";
import { GalleryManager } from "../components/admin/GalleryManager";
import { OffersManager } from "../components/admin/OffersManager";
import { ReviewsManager } from "../components/admin/ReviewsManager";
import { Logo } from "../components/site/Logo";
import { EnquiriesManager } from "../components/admin/EnquiriesManager";
import { Button } from "../components/ui/button";

const TABS = [
  { key: "menu", label: "Menu", icon: UtensilsCrossed },
  { key: "website", label: "Website", icon: Settings },
  { key: "gallery", label: "Gallery", icon: Images },
  { key: "offers", label: "Offers", icon: Tag },
  { key: "reviews", label: "Reviews", icon: Star },
  { key: "enquiries", label: "Enquiries", icon: Inbox },
];

export default function Admin() {
  const { admin, logout } = useAuth();
  const [tab, setTab] = useState("menu");

  if (admin === null)
    return <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground" data-testid="admin-loading">Loading…</div>;
  if (admin === false) return <AdminLogin />;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-border bg-charcoal/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-5 py-4 md:px-8">
          <Logo compact />
          <div className="flex items-center gap-2">
            <Link to="/" target="_blank" data-testid="admin-view-site-link"
              className="flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-xs text-cream/80 hover:text-caramel">
              <ExternalLink className="h-3.5 w-3.5" /> View site
            </Link>
            <Button variant="ghost" onClick={logout} data-testid="admin-logout-btn" className="text-cream/80 hover:text-caramel">
              <LogOut className="mr-1.5 h-4 w-4" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl gap-8 px-5 py-8 md:flex md:px-8">
        <nav className="flex gap-2 overflow-x-auto md:w-52 md:shrink-0 md:flex-col md:overflow-visible" data-testid="admin-nav">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
              className={`flex shrink-0 items-center gap-2 rounded-xl px-4 py-2.5 text-sm transition-colors ${
                tab === t.key ? "bg-copper text-charcoal" : "text-cream/70 hover:bg-card hover:text-caramel"
              }`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </nav>

        <main className="mt-8 min-w-0 flex-1 md:mt-0">
          {tab === "menu" && <MenuManager />}
          {tab === "website" && <SettingsManager />}
          {tab === "gallery" && <GalleryManager />}
          {tab === "offers" && <OffersManager />}
          {tab === "reviews" && <ReviewsManager />}
          {tab === "enquiries" && <EnquiriesManager />}
        </main>
      </div>
    </div>
  );
}
